import React, { useRef, useState, useEffect } from 'react';
import { Check, Globe, FileText, Calculator as CalcIcon, Briefcase, ArrowRight } from 'lucide-react';
import { nriServices } from '../data/mockData';
import { Link } from 'react-router-dom';

const NRISection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-32 bg-gradient-to-br from-gray-900 via-black to-gray-900 relative overflow-hidden">
      {/* Decorative Globe */}
      <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-[600px] h-[600px] opacity-10">
        <div className="absolute inset-0 border-2 border-white/20 rounded-full animate-spin-slow" />
        <div className="absolute inset-8 border border-white/10 rounded-full animate-spin-slow-reverse" />
        <div className="absolute inset-16 border border-white/5 rounded-full animate-spin-slow" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className={`transform transition-all duration-1000 ${
            isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
          }`}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10 mb-6">
              <Globe className="w-4 h-4 text-emerald-400" />
              <span className="text-white/60 text-sm">NRI Services</span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              NRI Connect
            </h2>

            <p className="text-white/60 text-lg leading-relaxed mb-8">
              At Frontier Investments, we understand the unique financial needs of
              Non-Resident Indians. Our dedicated NRI services are designed to offer
              end-to-end solutions that ensure compliance, growth, and peace of mind.
            </p>

            <div className="space-y-4 mb-10">
              {nriServices.map((service, index) => (
                <div
                  key={service.id}
                  className={`flex items-start gap-4 p-5 bg-white/5 rounded-2xl border border-white/10 hover:bg-white/10 transition-all duration-300 transform ${
                    isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                  }`}
                  style={{ transitionDelay: `${index * 100 + 300}ms` }}
                >
                  <div className="p-2 bg-emerald-500/20 rounded-lg flex-shrink-0">
                    <Check className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">{service.title}</h4>
                    <p className="text-white/50 text-sm">{service.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <Link
              to="/nri"
              className="inline-flex items-center gap-3 px-8 py-4 bg-white text-black font-semibold rounded-full hover:shadow-[0_0_40px_rgba(255,255,255,0.3)] transition-all duration-500 group"
            >
              Explore NRI Connect
              <ArrowRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </div>

          {/* Right Visual */}
          <div className={`relative transform transition-all duration-1000 delay-300 ${
            isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
          }`}>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&h=600&fit=crop"
                alt="NRI Services"
                className="rounded-3xl shadow-2xl"
              />
              {/* Floating Cards */}
              <div className="absolute -left-8 top-1/4 p-4 bg-white rounded-2xl shadow-xl animate-float">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <FileText className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">DTAA Benefits</p>
                    <p className="font-bold text-gray-800">Maximized</p>
                  </div>
                </div>
              </div>

              <div className="absolute -right-8 top-1/2 p-4 bg-white rounded-2xl shadow-xl animate-float-delay">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <CalcIcon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Tax Savings</p>
                    <p className="font-bold text-gray-800">Optimized</p>
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-6 left-1/4 p-4 bg-white rounded-2xl shadow-xl animate-float">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Briefcase className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Portfolio</p>
                    <p className="font-bold text-gray-800">Diversified</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes spin-slow-reverse {
          from { transform: rotate(360deg); }
          to { transform: rotate(0deg); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .animate-float-delay {
          animation: float 3s ease-in-out infinite;
          animation-delay: 1s;
        }
        .animate-spin-slow {
          animation: spin-slow 30s linear infinite;
        }
        .animate-spin-slow-reverse {
          animation: spin-slow-reverse 25s linear infinite;
        }
      `}</style>
    </section>
  );
};

export default NRISection;
